import { useMemo } from 'react';
import { Filter, Stack } from '@fashionnovateam/nova-ui-toolkit';
import { useHistory } from 'react-router-dom';
import type {
  FilterDefinition,
  AppliedFilterItem,
} from '@fashionnovateam/nova-ui-toolkit';
import { useParamFilter } from '../../../Lib/Hooks/Filters/useParamFilter';
import { possibleParams } from '../../../Lib/Hooks/ItemService/params.constants';
import { useGetMetaDatas } from '../../../Lib/Hooks/MetadataService';

type CategoryFilterValue = {
  property: 'category';
  value: string[];
};

type ClassFilterValue = {
  property: 'class';
  value: string[];
};

export function CategoryClassFilter() {
  const history = useHistory();
  const { paramFilters } = useParamFilter(possibleParams);

  const { data: metaDatas } = useGetMetaDatas(['ns_master_merchHierarchy']);

  const hierarchyLookup = useMemo(() => {
    if (!metaDatas?.ns_master_merchHierarchy?.divisions) {
      return { categories: [], categoryToClasses: new Map() };
    }

    const allCategories = new Set<string>();
    const categoryToClasses = new Map<string, Set<string>>();

    try {
      Object.values(metaDatas.ns_master_merchHierarchy.divisions).forEach(
        (division) => {
          if (!division || typeof division !== 'object') {
            console.warn('Invalid division structure:', division);
            return;
          }

          Object.values(division).forEach((department) => {
            if (!department || typeof department !== 'object') {
              console.warn('Invalid department structure:', department);
              return;
            }

            Object.entries(department).forEach(([category, classes]) => {
              if (!category || !classes) {
                console.warn('Invalid category/classes structure:', {
                  category,
                  classes,
                });
                return;
              }

              allCategories.add(category);

              if (!categoryToClasses.has(category)) {
                categoryToClasses.set(category, new Set());
              }

              if (Array.isArray(classes)) {
                classes.forEach((classValue: string) => {
                  if (classValue && typeof classValue === 'string') {
                    const categorySet = categoryToClasses.get(category);
                    if (categorySet) {
                      categorySet.add(classValue);
                    }
                  }
                });
              } else if (typeof classes === 'object') {
                Object.keys(classes).forEach((classValue: string) => {
                  if (classValue && typeof classValue === 'string') {
                    const categorySet = categoryToClasses.get(category);
                    if (categorySet) {
                      categorySet.add(classValue);
                    }
                  }
                });
              } else {
                console.warn('Invalid classes format (not array or object):', {
                  category,
                  classes,
                });
              }
            });
          });
        },
      );
    } catch (error) {
      console.error('Error processing hierarchy metadata:', error);
      return { categories: [], categoryToClasses: new Map() };
    }

    return {
      categories: Array.from(allCategories),
      categoryToClasses,
    };
  }, [metaDatas]);

  const categoriesOptions = useMemo(
    () =>
      hierarchyLookup.categories.map((category) => ({
        label: category,
        value: category,
      })),
    [hierarchyLookup],
  );

  const validCategories = useMemo(() => {
    if (!paramFilters.category?.length) return [];

    const validCategorySet = new Set(hierarchyLookup.categories);
    return paramFilters.category.filter((category) =>
      validCategorySet.has(category),
    );
  }, [paramFilters.category, hierarchyLookup.categories]);

  const validClasses = useMemo(() => {
    if (!paramFilters.class?.length || !validCategories.length) return [];

    const validClassSet = new Set<string>();
    validCategories.forEach((category) => {
      const classes = hierarchyLookup.categoryToClasses.get(category);
      if (classes) {
        classes.forEach((classValue: string) => validClassSet.add(classValue));
      }
    });

    return paramFilters.class.filter((classValue) =>
      validClassSet.has(classValue),
    );
  }, [paramFilters.class, validCategories, hierarchyLookup.categoryToClasses]);

  const classOptions = useMemo(() => {
    if (!validCategories.length || !hierarchyLookup.categoryToClasses.size) {
      return [];
    }

    const allClasses = new Set<string>();
    const classToCategories = new Map<string, Set<string>>();

    validCategories.forEach((category) => {
      const classes = hierarchyLookup.categoryToClasses.get(category);
      if (classes) {
        classes.forEach((classValue: string) => {
          allClasses.add(classValue);

          if (!classToCategories.has(classValue)) {
            classToCategories.set(classValue, new Set());
          }
          const classSet = classToCategories.get(classValue);
          if (classSet) {
            classSet.add(category);
          }
        });
      }
    });

    return Array.from(allClasses)
      .map((classValue) => {
        const parentCategories = Array.from(
          classToCategories.get(classValue) || [],
        );
        const label = `${classValue} (${parentCategories.join(', ')})`;
        return { label, value: classValue };
      })
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [validCategories, hierarchyLookup]);

  const isClassValidForCategory = (
    classValue: string,
    categoryName: string,
  ) => {
    const classes = hierarchyLookup.categoryToClasses.get(categoryName);
    return classes ? classes.has(classValue) : false;
  };

  const filterDefinitions = useMemo<FilterDefinition[]>(() => {
    const definitions: FilterDefinition[] = [
      {
        id: 'category',
        label: 'Category',
        type: 'checkbox',
        options: categoriesOptions,
      },
    ];

    if (paramFilters.category && paramFilters.category.length > 0) {
      definitions.push({
        id: 'class',
        label: 'Class',
        type: 'checkbox',
        options: classOptions,
      });
    }

    return definitions;
  }, [paramFilters.category, categoriesOptions, classOptions]);

  const appliedFilters = useMemo<AppliedFilterItem[]>(() => {
    const filters: AppliedFilterItem[] = [];

    if (validCategories.length) {
      filters.push({
        id: 'category-1',
        property: 'category',
        rule: 'in',
        value: validCategories,
      });
    }

    if (validClasses.length) {
      filters.push({
        id: 'class-1',
        property: 'class',
        rule: 'in',
        value: validClasses,
      });
    }

    return filters;
  }, [validCategories, validClasses]);

  const handleFilterChange = (newFilters: AppliedFilterItem[]) => {
    const searchParams = new URLSearchParams(window.location.search);

    searchParams.delete('category');
    searchParams.delete('class');

    if (newFilters.length > 0) {
      const categoryFilter = newFilters.find(
        (f): f is AppliedFilterItem & CategoryFilterValue =>
          f.property === 'category',
      );
      const classFilter = newFilters.find(
        (f): f is AppliedFilterItem & ClassFilterValue =>
          f.property === 'class',
      );

      if (categoryFilter?.value.length) {
        searchParams.set('category', categoryFilter.value.join(','));
      }

      if (classFilter?.value.length && categoryFilter?.value.length) {
        const filteredClasses = classFilter.value.filter((classValue) =>
          categoryFilter.value.some((cat) =>
            isClassValidForCategory(classValue, cat),
          ),
        );

        if (filteredClasses.length > 0) {
          searchParams.set('class', filteredClasses.join(','));
        }
      }
    }

    history.push({
      pathname: window.location.pathname,
      search: searchParams.toString(),
    });
  };

  return (
    <Stack
      direction="col"
      className="bg-neutral-100 p-2 xl:bg-neutral-0 border-b nu-border-neutral-200 xl:px-6 sticky top-0 left-0 z-10 "
    >
      <div className="relative">
        <Filter
          filterDefinitions={filterDefinitions}
          filters={appliedFilters}
          onFilterChange={handleFilterChange}
          allowDuplicateFilter={false}
          showSelectAll={false}
        />
      </div>
    </Stack>
  );
}
